<template>
  <div>
    <!-- HEADER -->
    <section class="mb-4 mb-lg-5">
      <div class="container" ref="postHeader">
        <div
          class="image-header"
          :class="{ 'rounded-regular': !authenticated }"
          :style="coverImage"
        ></div>
      </div>
    </section> <!-- END HEADER -->

    <!-- ACTIONS -->
    <section class="mb-3 mb-md-5" v-if="post">
      <div
        class="container text-center d-flex flex-column flex-lg-row justify-content-center align-items-lg-center"
        ref="postActions"
      >
        <div :class="{'background-white': !nightMode, 'background-black': nightMode}" class="shadow-light rounded-xl p-2 pl-4 align-self-center d-flex align-items-center min-height-wrap">
          <div class="d-flex flex-wrap flex-md-nowrap flex-lg-wrap justify-content-center justify-content-md-between align-items-center">
            <!-- CATEGORY OR BOOK -->
            <template
              v-if="post.type === 'post'"
            >
              <router-link
                :to="{ name: 'categories.show', params: { username: post.user.username, slug: post.category ? post.category.slug : 'category' } }"
                v-if="post.category"
              >
                <div class="d-flex justify-content-center mr-4">
                  <span class="text-color2 d-flex align-items-center">
                    <BaseIcon>
                      <TagIcon />
                    </BaseIcon>
                    <span class="text-color2 font-size-small">{{ post.category.name }}</span>
                  </span>
                </div>
              </router-link>
              <router-link
                :to="{ name: 'groups.show', params: { slug: post.group.slug } }"
                v-if="post.group"
              >
                <div class="d-flex justify-content-center mr-4">
                  <span class="text-color2 d-flex align-items-center">
                    <span class="icon-basic mr-2">
                      <i class="fas fa-campground"></i>
                    </span>
                    <span class="text-color2 font-size-small">{{ post.group.name }}</span>
                  </span>
                </div>
              </router-link>
            </template>
            <template
              v-else-if="post.type === 'chapter'"
            >
              <router-link
                :to="{ name: 'books.show', params: { username: post.user.username, slug: post.book.slug } }"
              >
                <div class="d-flex justify-content-center mr-4">
                  <span class="text-color2 d-flex align-items-center">
                    <span class="icon-basic text-color2 mr-2">
                      <i class="fa fa-book"></i>
                    </span>
                    <span class="text-color2 font-size-small" :title="post.book.title">{{ post.book.title.length > 32 ? `${post.book.title.substr(0,32)}...` : post.book.title }}</span>
                  </span>
                </div>
              </router-link>
            </template>
            <div class="d-flex justify-content-center mr-4">
              <span class="text-color2 d-flex align-items-center" v-if="post">
                <BaseIcon>
                  <ClockIcon v-if="post.status === 'scheduled'" />
                  <CalendarIcon v-else />
                </BaseIcon>
                <span class="font-size-small">{{ post.published_at | formatDate(post.status === 'scheduled' ? 'DD/MM/YYYY HH:mm' : 'DD/MM/YYYY') }}</span>
              </span>
            </div>
            <div class="d-flex justify-content-center align-items-center">
              <div
                class="d-flex justify-content-center"
                :class="{ 'mr-4': post && user.id === post.user.id }"
                v-if="user && post && post.status === 'published'"
              >
                <Button
                  class="btn btn-outline-color2 px-2 hover-color-white favorite-button"
                  :class="{ active: post.is_favorited }"
                  @click="toggleFavorite"
                  :loading="favoriteLoader"
                >
                  <BaseIcon>
                    <HeartFilledIcon v-if="post.is_favorited" />
                    <HeartIcon v-else />
                  </BaseIcon>
                </Button>
              </div>
              <div
                class="d-flex justify-content-center"
                v-else-if="post && post.status === 'published'"
              >
                <Button
                  class="btn btn-outline-color2 px-2 hover-color-white favorite-button"
                  @click="likePostButtonClick"
                >
                  <BaseIcon>
                      <HeartIcon />
                    </BaseIcon>
                </Button>
              </div>
              <div class="d-flex justify-content-center flex-grow-1">
                <template
                  v-if="post && user && post.user.id === user.id"
                >
                  <router-link
                    class="btn btn-outline-color2"
                    :to="editLink"
                  >
                    Editar
                  </router-link>
                </template>
              </div>
            </div>
          </div>
        </div>
        <div
          class="d-flex align-items-center flex-wrap flex-md-nowrap justify-content-center mt-3 mt-lg-0"
          v-if="post"
        >
          <UserTag
            class="ml-md-2 align-self-center mb-3 mb-md-0"
            :user="post.user"
          />
          <SaveToListTag
            class="ml-2 mr-2 align-self-center mb-3 mb-md-0"
            type="post"
            :target="post.id"
            v-if="user && post.status === 'published'"
          />
          <!-- <UserDonationButton
            :user="post.user"
            class="mr-2 mb-3 mb-md-0"
            color="btn-outline-color2"
          /> -->
          <ShareTag
            class="mr-2 align-self-center mb-3 mb-md-0"
            type="post"
            :target="post"
            v-if="post.status === 'published'"
          />
          <div v-if="user && user.id !== post.user.id">
            <Button
              class="btn-sm btn-outline-color2 px-3 mb-3 mb-md-0"
              v-b-modal.create-denounce
            >
              Denunciar
            </Button>
          </div>
          <div
            class="d-block d-md-inline-block ml-md-2 "
            v-else-if="post && user && user.id === post.user.id"
          >
            <Button
              class="btn-circle btn-outline-color2 d-inline-block mb-3 mb-md-0"
              v-b-modal="'destroy-' + post.type + '-' + post.id"
            >
              <i class="fa fa-trash"></i>
            </Button>
          </div>
        </div>
      </div>
    </section> <!-- END ACTIONS -->

    <DestroyPostModal
      :type="post && post.type"
      :id="post && post.id"
      :post="post"
    />

    <DenounceModal
      title="Denunciar Publicação"
      type="post"
      :target="post.id"
      v-if="post"
    >
      <template v-slot:description>
        <p class="text-center font-size-small">
          Selecione a opção que melhor se encaixa com a sua denúncia. Nossa equipe avaliará o ocorrido e tomará as medidas necessárias.
        </p>
      </template>
    </DenounceModal>

    <!-- POST DATA -->
    <section class="mb-4" v-if="post">
      <div class="container text-center">
        <div class="col-lg-10 mx-auto">
          <h2 class="font-size-medium font-weight-bold mb-3">
            <BaseIcon
              v-if="post && user && post.visibility === 'private' && post.user.id === user.id"
            >
              <LockIcon />
            </BaseIcon>
            {{ post && post.title }}
            <template v-if="post.status === 'scheduled'">
              (agendado)
            </template>
            <template v-else-if="post.status === 'draft'">
              (rascunho)
            </template>
          </h2>
          <div class="d-flex align-items-center justify-content-center">
            <span
              class="mr-4 font-size-small"
              v-b-modal.people-favorited
            >
              <BaseIcon
                size="xSmall"
              >
                <HeartFilledIcon />
              </BaseIcon>
              {{ post && post.favorites_count | formatNumber }}
            </span>
            <span class="mr-4 font-size-small">
              <BaseIcon
                size="xSmall"
              >
                <EyeIcon />
              </BaseIcon>
              {{ post && post.views_count | formatNumber }}
            </span>
            <span class="mr-4 font-size-small">
              <BaseIcon
                size="xSmall"
              >
                <CommentBubbleIcon />
              </BaseIcon>
              {{ post && post.comments_count | formatNumber }}
            </span>
            <span class=" font-size-small">
              <BaseIcon
                size="xxSmall"
              >
                <HourglassIcon />
              </BaseIcon>
              {{ post && post.reading_time }}
            </span>
          </div>
          <PeopleFavoritedModal
            :target="post.id"
            type="post"
          />
        </div>
      </div>
    </section> <!-- END POST DATA -->

    <!-- AWARDS -->
    <section
      class="mb-4 awards"
      v-if="awards.length > 0"
    >
      <div class="container">
        <AwardList
          type="post"
          :target="post.id"
          :awards="awards"
        />
      </div>
    </section> <!-- END AWARDS -->

    <!-- POST CONTENT -->
    <section
      v-if="post && post.is_sensible && !confirmReading"
    >
      <div class="container container-resize">
        <div :class="{'background-white': !nightMode, 'background-black': nightMode}" class="content-card shadow-light rounded-default p-4 text-center">
          <div class="col-12 col-lg-10 mx-auto">
            <strong><p>O conteúdo desta publicação foi marcado pelo(a) autor(a) como sensível.</p></strong>
            <p>Conteúdos sensíveis englobam assuntos que não são indicados para menores de 18 anos, bem como temas que podem gerar reações emocionais adversas no(a) leitor(a)</p>
            <p>Para mais informações, consulte as <router-link :to="{ name: 'statics.moderation' }" class="link-color2" target="_blank">Regras de Moderação</router-link>.</p>
          </div>
          <div class="col-12 col-lg-10 mx-auto">
            <Button
              class="btn btn-outline-color2 px-2 hover-color-white col-9 col-offset-3 col-md-4 col-md-offset-2 mr-2"
              @click="clickConfirmReading"
            >Continuar lendo
            </Button>
          </div>
        </div>
      </div>
    </section>
    <section v-else>
      <div class="container container-resize">
        <div class="col-12 col-lg-10 mx-auto" ref="postContent">
          <template v-if="parsedContent && isContentJson">
            <BaseBlock
              v-for="(content, index) in parsedContent.content"
              :key="index"
              :content="content"
            />
          </template>
          <template
            v-else
          >
            <div class="old" v-html="parsedContent"></div>
          </template>
        </div>
      </div>
    </section> <!-- END POST CONTENT -->

    <!-- PREVIOUS & NEXT POST -->
    <section
      class="mt-4"
      v-if="post && post.type === 'chapter' && post.status === 'published' && post.visibility === 'public' && post.chapter"
    >
      <div class="container">
        <div class="col-12 col-lg-10 mx-auto d-flex flex-column flex-md-row justify-content-center justify-content-md-between">
          <div class="">
            <router-link
              :to="{ name: 'chapters.show', params: { bookSlug: post.book.slug, slug: post.chapter.previous_chapter.content.slug } }"
              v-if="post.chapter.previous_chapter"
            >
              <div
                class="d-flex align-items-center link-color1"
              >
                <div class="text-color2 font-size-xxLarge mr-4">
                  <i class="fa fa-angle-left"></i>
                </div>
                <div>
                  <span class="d-block font-size-xSmall">Capítulo anterior</span>
                  <span class="d-block font-weight-bold">{{ post.chapter.previous_chapter.content.title }}</span>
                </div>
              </div>
            </router-link>
          </div>
          <div class="">
            <router-link
              :to="{ name: 'chapters.show', params: { bookSlug: post.book.slug, slug: post.chapter.next_chapter.content.slug } }"
              v-if="post.chapter.next_chapter"
            >
              <div
                class="d-flex align-items-center justify-content-end link-color1"
              >
                <div>
                  <span class="d-block font-size-xSmall text-right">Próximo capítulo</span>
                  <span class="d-block font-weight-bold">{{ post.chapter.next_chapter.content.title }}</span>
                </div>
                <div class="text-color2 font-size-xxLarge ml-4">
                  <i class="fa fa-angle-right"></i>
                </div>
              </div>
            </router-link>
          </div>
        </div>
      </div>
    </section> <!-- END PREVIOUS & NEXT POST -->

    <section class="mt-3">
      <div class="container">
        <div class="d-flex flex-column flex-md-row col-12 col-lg-10 mx-auto">
          <!-- TAGS -->
          <div class="col-12 col-md-9 text-center text-md-left order-2 order-md-1" v-if="post && post.tags.length > 0">
            <div>
              <h2 class="font-weight-bold font-size-medium">Tags</h2>
              <ul class="tag-list d-flex mt-3 flex-wrap justify-content-center justify-content-md-start">
                <li class="tag-item mb-2 mr-4"
                  v-for="tag in post.tags"
                  :key="tag.id"
                >
                  <router-link
                    :to="{ name: 'tags.show', params: { slug: tag.slug } }"
                  >
                    <span class="tag">{{ tag.name }}</span>
                  </router-link>
                </li>
              </ul>
            </div>
          </div><!-- TAGS -->
          <!-- LICENSE -->
          <div
            class="col-12 col-md-3 text-center order-1 order-md-2 mb-3 mb-md-0"
            :class="{
              'text-md-right': post.tags.length > 0,
              'text-md-left': post.tags.length <= 0
            }"
            v-if="post && post.license"
          >
            <div>
              <h2 class="font-weight-bold font-size-medium">
                Licença
                <i
                  class="fa fa-info-circle cursor-pointer text-color2 font-size-small"
                  v-b-tooltip.hover="{ customClass: 'font-size-xSmall' }"
                  :title="licenseTooltipText"
                ></i>
              </h2>
              <span class="text-uppercase" v-b-tooltip.hover="{ customClass: 'font-size-xSmall' }" :title="licenseTipText" placement="bottom">
                <i class="text-color2" :class="licenseClasses"></i> {{ post && post.license }}
              </span>
            </div>
          </div><!-- END LICENSE -->
        </div>
      </div>
    </section>

    <section class="mt-3" v-if="post && post.status === 'published'">
      <div class="container">
        <hr
          class="is-empty h-1 col-10 mx-auto my-4"
        />
        <div class="col-12 col-lg-10 mx-auto">
          <h2 class="font-weight-bold font-size-medium mb-4">Gostou da publicação?</h2>
          <Button
            class="btn btn-outline-color2 px-2 hover-color-white col-6 col-offset-3 col-md-2 col-md-offset-2 mr-2"
            :class="{ disabled: !post }"
            type="post"
            :target="post"
            v-b-modal="'share-' + post.type + '-' + post.id"
          >
            Compartilhar
          </Button>
          <Button
            class="btn btn-outline-color2 px-2 hover-color-white favorite-button ml-2"
            :class="{ active: post.is_favorited }"
            @click="toggleFavorite"
            :loading="favoriteLoader"
            v-if="user"
          >
            <BaseIcon>
              <HeartFilledIcon v-if="post.is_favorited" />
              <HeartIcon v-else />
            </BaseIcon>
          </Button>
        </div>
      </div>
    </section>
    <!-- AUTHOR DATA -->
    <section class="mt-5" v-if="post">
      <div class="container">
        <div :class="{'background-white': !nightMode, 'background-black': nightMode}" class="author-inside-card rounded-regular shadow-light p-4 d-flex flex-column flex-lg-row align-items-center justify-content-between">
          <div class="col-lg-9 pr-0 pl-0">
            <router-link :to="{ name: 'users.show', params: { username: post ? post.user.username : 'username' } }">
              <div class="d-flex flex-column flex-lg-row align-items-center justify-content-center justify-content-lg-start">
                <div class="mr-lg-4">
                  <div class="author-image-circle image-lg background-cover shadow-light" :style="profileImage"></div>
                </div>
                <div class="text-center text-lg-left">
                  <span class="font-size-medium font-weight-bold text-color1 d-flex justify-content-center justify-content-lg-start align-items-center">
                    {{ post && post.user.name }}
                  </span>
                  <span class="text-color1"><span class="text-color2">@</span>{{ post && post.user.username }}</span>
                  <BaseIcon
                    v-if="post && post.user.is_verified"
                    size="xSmall"
                    class="verified-wrap ml-1"
                  >
                    <VerifiedIcon
                      class="profile"
                    />
                  </BaseIcon>
                  <p class="font-size-small text-color1">{{ post && post.user.description }}</p>
                </div>
              </div>
            </router-link>
          </div>
          <div class="d-flex flex-wrap flex-column">
            <div class="text-center mb-3 d-flex actions-to-user">
              <!-- <UserDonationButton
                :user="post.user"
                id="author-post-desc"
                color="btn-outline-color2"
              /> -->
              <FollowButton
                class="ml-2"
                :isFollowed="post.user.is_followed"
                :idToFollow="post.user.id"
                @follow-toggled="toggleFollow"
                v-if="post && user"
              />
              <NotificationButton
                class="ml-2"
                :isToNotify="post.user.is_notification"
                :isFollowed="post.user.is_followed"
                :idToFollow="post.user.id"
                @follow-toggled="toggleNotification"
                v-if="post && user"
              />
            </div>
            <div v-if="post && post.user.social">
              <ul class="d-flex justify-content-center justify-content-lg-end align-items-center mb-0 social-listing">
                <li class="mr-2" v-if="post.user.social.twitter">
                  <a :href="post.user.social.twitter" class="rounded-xl bordered-1 border-color1 text-color1 social-icon" target="_blank"><i class="fa fa-twitter"></i></a>
                </li>
                <li class="mr-2" v-if="post.user.social.facebook">
                  <a :href="post.user.social.facebook" class="rounded-xl bordered-1 border-color1 text-color1 social-icon" target="_blank"><i class="fa fa-facebook"></i></a>
                </li>
                <li class="mr-2" v-if="post.user.social.instagram">
                  <a :href="post.user.social.instagram" class="rounded-xl bordered-1 border-color1 text-color1 social-icon" target="_blank"><i class="fa fa-instagram"></i></a>
                </li>
                <li class="mr-2" v-if="post.user.social.linkedin">
                  <a :href="post.user.social.linkedin" class="rounded-xl bordered-1 border-color1 text-color1 social-icon" target="_blank"><i class="fa fa-linkedin"></i></a>
                </li>
                <li
                  class="mr-2"
                  v-if="post.user.social.youtube"
                >
                  <a :href="post.user.social.youtube" class="rounded-xl bordered-1 border-color1 text-color1 social-icon" target="_blank"><i class="fa fa-youtube-play"></i></a>
                </li>
                <li
                  class="mr-2"
                  v-if="post.user.social.external"
                >
                  <a :href="post.user.social.external" class="rounded-xl bordered-1 border-color1 text-color1 social-icon" target="_blank"><i class="fa fa-link"></i></a>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </section> <!-- END AUTHOR DATA -->

    <!-- COMMENTS -->
    <section class="mt-5" v-if="post && post.is_comments_enabled && post.status === 'published'">
      <div class="container">
        <div class="col-12 col-lg-10 mx-auto text-center text-lg-left">
          <h2 class="font-weight-bold font-size-medium">Comentários</h2>

          <CommentSection
            :entityId="post.id"
            entityType="post"
            :isAuthor="currentUser && (post.user.id === currentUser.id)"
          />
        </div>
      </div>
    </section> <!-- END COMMENTS -->

    <!-- SEE MORE DATA -->
    <section class="mt-5" v-if="post && post.status === 'published' && morePosts && morePosts.length > 0">
      <div class="container">
        <hr
          class="is-empty h-1 col-10 mx-auto my-4"
        />
        <div class="col-12 col-lg-10 mx-auto">
          <h2 class="font-weight-bold font-size-medium mb-4">Outras publicações de mesma autoria</h2>
          <div>
            <PostThumbCarousel
              :posts="morePosts"
            />
          </div>
        </div>
      </div>
    </section><!-- END SEE MORE DATA -->

    <!-- SEE SIMILAR DATA -->
    <section class="mt-5" v-if="post && post.status === 'published' && similarPosts && similarPosts.length > 0">
      <div class="container">
        <hr
          class="is-empty h-1 col-10 mx-auto my-4"
        />
        <div class="col-12 col-lg-10 mx-auto">
          <h2 class="font-weight-bold font-size-medium mb-4">Você pode gostar de...</h2>
          <div>
            <PostThumbCarousel
              :posts="similarPosts"
            />
          </div>
        </div>
      </div>
    </section><!-- END SEE SIMILAR DATA -->
  </div>
</template>

<script>

import { mapState } from 'vuex'
import AwardsService from '@/modules/awards/services/awards-service'
import PostsService from '@/modules/posts/services/posts-service'
import FavoritesService from '@/modules/favorites/services/favorites-service'
import variables from '@/mixins/variables'
import seo from '@/mixins/seo'

import AwardList from '@/components/awards/AwardList'
import Button from '@/components/common/Button/Button.vue'
import CommentSection from '@/components/comment/CommentSection'
import DestroyPostModal from '@/components/post/DestroyPostModal.vue'
import DenounceModal from '@/components/denounces/DenounceModal.vue'
import FollowButton from '@/components/follow/FollowButton.vue'
import NotificationButton from '@/components/follow/NotificationButton.vue'
import PeopleFavoritedModal from '@/components/favorites/PeopleFavoritedModal.vue'
import PostThumbCarousel from '@/components/post/PostThumbCarousel.vue'
import SaveToListTag from '@/components/lists/SaveToListTag.vue'
import ShareTag from '@/components/share/ShareTag.vue'
import UserTag from '@/components/user/UserTag.vue'
// import UserDonationButton from '@/components/user/UserDonationButton'

import BaseBlock from '@/components/post/Blocks/BaseBlock.vue'

import BaseIcon from '@/components/common/Icon/BaseIcon.vue'
import CalendarIcon from '@/components/common/Icon/CalendarIcon.vue'
import ClockIcon from '@/components/common/Icon/ClockIcon.vue'
import CommentBubbleIcon from '@/components/common/Icon/CommentBubbleIcon.vue'
import EyeIcon from '@/components/common/Icon/EyeIcon.vue'
import HeartIcon from '@/components/common/Icon/HeartIcon.vue'
import HeartFilledIcon from '@/components/common/Icon/HeartFilledIcon.vue'
import HourglassIcon from '@/components/common/Icon/HourglassIcon.vue'
import LockIcon from '@/components/common/Icon/LockIcon.vue'
import TagIcon from '@/components/common/Icon/TagIcon.vue'
import VerifiedIcon from '@/components/common/Icon/VerifiedIcon.vue'

export default {
  components: {
    AwardList,
    BaseBlock,
    BaseIcon,
    Button,
    CalendarIcon,
    ClockIcon,
    CommentBubbleIcon,
    CommentSection,
    DestroyPostModal,
    DenounceModal,
    EyeIcon,
    FollowButton,
    NotificationButton,
    HeartIcon,
    HeartFilledIcon,
    HourglassIcon,
    LockIcon,
    PeopleFavoritedModal,
    PostThumbCarousel,
    SaveToListTag,
    ShareTag,
    TagIcon,
    VerifiedIcon,
    // UserDonationButton,
    UserTag
  },
  mixins: [variables, seo],
  data () {
    return {
      post: null,
      awards: [],
      comments: [],
      commentsPage: 1,
      mainLoader: false,
      morePosts: [],
      similarPosts: [],
      parentCommentsLoader: false,
      favoriteLoader: false,
      postsLoader: false,
      isLoadMoreParentCommentsVisible: true,
      confirmReading: false,
      scrollDebounce: null
    }
  },
  metaInfo () {
    return {
      title: this.post ? this.post.title : '',
      meta: [
        { vmid: 'description', name: 'description', content: this.post && this.post.description ? this.post.description : this.seoDefaultDescription },
        { vmid: 'og-description', name: 'og:description', content: this.post && this.post.description ? this.post.description : this.seoDefaultDescription },
        { vmid: 'og-title', name: 'og:title', content: this.post ? `${this.post.title} | ${this.seoDefaultTitleAppend}` : `${this.seoDefaultTitle} | ${this.seoDefaultTitleAppend}` },
        { vmid: 'og-image', name: 'og:image', content: this.post && this.post.has_cover ? this.post.cover : this.seoDefaultImage }
      ]
    }
  },
  computed: {
    ...mapState({
      nightMode: state => state.nightMode
    }),
    ...mapState('users', {
      currentUser: state => state.user
    }),
    editLink () {
      const route = {}
      if (this.post) {
        if (this.post.type === 'chapter') {
          route.name = 'chapters.edit'
          route.params = { bookSlug: this.post.book.slug, slug: this.post.slug }
        } else if (this.post.group) {
          route.name = 'groups.posts.edit'
          route.params = { groupSlug: this.post.group.slug, slug: this.post.slug }
        } else {
          route.name = 'posts.edit'
          route.params = { username: this.post.user.username, slug: this.post.slug }
        }
        const props = this.$router.resolve(route)
        return props.href
      }
      return '/'
    },
    parsedContent () {
      if (!this.post) return ''
      try {
        return JSON.parse(this.post.content)
      } catch (e) {
        return this.post.content
      }
    },
    isContentJson () {
      if (!this.post) return false
      try {
        JSON.parse(this.post.content)
        return true
      } catch (e) {
        return false
      }
    },
    coverImage () {
      return {
        backgroundImage: this.post ? `url('${this.post.cover}')` : `url('${this.noCoverUrl}')`
      }
    },
    profileImage () {
      return {
        backgroundImage: this.post ? `url('${this.post.user.avatar}')` : `url('${this.noAvatarUrl}')`
      }
    },
    ...mapState('users', {
      user: state => state.user
    }),
    ...mapState('auth', {
      authenticated: state => state.authenticated
    }),
    selectedLicense () {
      if (!this.post || !this.post.license) return null
      return this.licenseOptions.find(item => {
        return item.key === this.post.license
      })
    },
    licenseTipText () {
      if (!this.selectedLicense) return ''
      return this.selectedLicense.description || ''
    },
    licenseClasses () {
      if (!this.selectedLicense) return []
      return [this.selectedLicense.icon]
    }
  },
  created () {
    this.getPost()
  },
  mounted () {
    // Prevent copy paste
    this.$refs.postContent.addEventListener('copy', (event) => {
      const selection = document.getSelection().toString()
      const newContent = `${selection.substr(0, 100)}... Leia mais em ${window.location.href}`
      event.clipboardData.setData('text/plain', newContent)
      event.preventDefault()
    })

    // debounce variable to stop scroll event bubbling
    window.addEventListener('scroll', this.scrollListener, true)
  },
  beforeDestroy () {
    if (this.post) this.removeDisableCopy()
    window.removeEventListener('scroll', this.scrollListener, true)
  },
  watch: {
    $route (to, from) {
      this.reset()
      this.getPost()
    }
  },
  methods: {
    disableCopy (event) {
      const selection = document.getSelection().toString()
      const newContent = `${selection.substr(0, 100)}... Leia mais em ${window.location.href}`
      event.clipboardData.setData('text/plain', newContent)
      event.preventDefault()
    },
    removeDisableCopy () {
      this.$refs.postContent.removeEventListener('copy', this.disableCopy)
    },
    scrollListener () {
      if (this.authenticated) {
        clearTimeout(this.scrollDebounce)
        this.scrollDebounce = setTimeout(() => {
          this.calcReadingProgress()
        }, 750)
      }
    },
    clickConfirmReading () {
      this.confirmReading = !this.confirmReading
    },
    async getPost (getComments = true, getMorePosts = true, getAwards = true) {
      if (this.post) this.removeDisableCopy()
      try {
        const post = await PostsService.getPost(this.$route.params.slug)
        this.post = Object.assign({}, post)

        // Prevent copy paste
        if (this.post.license === 'cc-by-nc-nd' || this.post.license === null) {
          this.$refs.postContent.addEventListener('copy', this.disableCopy, true)
        }

        if (getMorePosts) {
          this.getMorePosts()
          this.getSimilarPosts()
        }
        if (getAwards) this.getAwards()
        // if (getComments) this.getComments()
      } catch (error) {
        error.handleGlobally && error.handleGlobally()
        if (this.$route.name === 'chapters.show') {
          this.$router.push({ name: 'books.show', params: { username: this.$route.params.username, slug: this.$route.params.bookSlug } })
        } else {
          if (window.history.length > 2) {
            this.$router.go(-1)
          } else {
            this.$router.push('/')
          }
        }
      }
    },
    async getMorePosts () {
      this.postsLoader = true
      try {
        if (this.post && this.post.user) {
          const extraPosts = await PostsService.getPosts({ userId: this.post.user.id, visibility: 'public', isPublished: true, excludedId: this.post.id })
          this.morePosts = extraPosts
        }
      } catch (error) {
        error.handleGlobally && error.handleGlobally()
      }
      this.postsLoader = false
    },
    async getSimilarPosts () {
      this.postsLoader = true
      try {
        if (this.post && this.post.tags.length > 0) {
          // busca uma tag aleatória vinculada ao post, para usar de parâmetro de busca
          const taglist = [...this.post.tags]
          let similarPosts = []
          while (!(similarPosts.length > 0) && taglist.length > 0) {
            const tagIndex = Math.floor(Math.random() * taglist.length)
            const rndTag = taglist[tagIndex]
            similarPosts = await PostsService.getPosts({ tagId: rndTag.id, visibility: 'public', isPublished: true, excludedId: this.post.id })
            // garantir que o texto atual não apareça dentre os recomendados
            similarPosts.splice(similarPosts.findIndex(v => v.id === this.post.id), 1)
            // se recebermos mais do que 10 textos de volta, limitar
            if (similarPosts.length > 10) similarPosts.length = 10
            // remover tag da lista temporária, para garantir que não seja buscada novamente caso seja vazia
            taglist.splice(tagIndex, 1)
          }
          // quando tivermos a array de similares, atribuir a lista que vai pro carousel
          this.similarPosts = similarPosts
        }
      } catch (error) {
        error.handleGlobally && error.handleGlobally()
      }
      this.postsLoader = false
    },
    async getAwards () {
      try {
        const awards = await AwardsService.getAwards({ type: 'post', target: this.post.id })
        this.awards = awards
      } catch (error) {
        error.handleGlobally && error.handleGlobally()
      }
    },
    async toggleFavorite () {
      this.favoriteLoader = true
      try {
        const action = this.post.is_favorited ? 'destroy' : 'create'
        await FavoritesService.toggleFavorite(this.post.id, 'post', action)
        this.post.is_favorited = !this.post.is_favorited
        this.getPost(false, false, false)
      } catch (error) {
        error.handleGlobally && error.handleGlobally()
      }
      this.favoriteLoader = false
    },
    calcReadingProgress () {
      if (this.$refs.postContent) {
        const elem = this.$refs.postContent
        const rect = elem.getBoundingClientRect()
        const headerHeight = document.getElementById('mainHeader').clientHeight
        let top = rect.top - headerHeight
        // Passed top border of content
        if (top < 0) {
          top = Math.abs(top)
          const percentage = Math.round(top / rect.height * 100)
          if (percentage < 130) {
            PostsService.updateProgress(this.post.id, percentage)
          }
        }
      }
    },
    likePostButtonClick () {
      if (!this.authenticated) {
        this.$bvModal.show('auth-modal')
      }
    },
    toggleFollow () {
      this.post.user.is_followed = !this.post.user.is_followed
    },
    toggleNotification () {
      this.post.user.is_followed = !this.post.user.is_followed
    },
    reset () {
      this.post = null
      this.mainLoader = false
      this.favoriteLoader = false
    },
    trimCopyPaste () {
      console.log('oi')
    }
  }
}
</script>

<style lang="scss" scoped>
.container{
  &.container-resize{
    max-width: 880px;

    // &.content-card{
    //   padding: 20px 3px;
    //   border-radius: 10px;
    //   overflow: hidden;
    //   box-shadow: 0 0 10px 0 rgb(0 0 0 / 30%);
    //   background-color: #fff;
    //   position: relative;
    //   text-align: center;
    // }
  }
}
.favorite-button {
  height: 44px;
  width: 46px;
  ::v-deep {
    & > span {
      display: block;
      margin-top: -1px;
    }
  }
}

.min-height-wrap {
  min-height: 56px;
}

.social-listing {
  li {
    &:last-child {
      margin-right: 0 !important;
    }
  }
}

.verified-wrap {
  height: 28px;
}

.actions-to-user{
  margin: 0 auto;
}

.old {
  ::v-deep {
    ul,
    ol {
        padding-left: $spacing_large;
        li {
          list-style: disc;
        }
    }

    li > p,
    li > ol,
    li > ul {
        margin: 0;
    }
    h2, h3, h4 {
      font-weight: bold;;
    }

    a {
      color: $color2;

      &:hover {
        color:$color1;
      }
    }

    img {
      display: block;
      width: auto;
      max-width: 100%;
      margin: 0 auto;
    }
  }
}
</style>
